import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useStoreState } from '@/stores/store';
import { prepareJson } from '../utils/prepareJson';
import cloneDeep from 'lodash/cloneDeep';
import { useNavigation } from '@react-navigation/native';
import { hasOwnProperty } from '@/utils/utils';

import store from '../stores/store';
import { isPatientApp } from '@/config/constants';
import starUtil from '@/utils/starUtil';
import { useDialog } from './useDialog';
import { useAudioContext } from '@/contexts/AudioContext/useAudioContext';
import { logError } from '@/utils/logHandling';
import { PREPARED_DIALOGS } from '@/components/ui/shared/Dialog/constants';

const { touched, stop, uploadResults, getTodaysProgress, upload, applyUserId } =
  starUtil;

const DEFAULT_STATE = {
  nextButton: 'Überspringen',
  exerciseid: 0,
  currentExercise: 0,
  lastExercise: 0,
  exercises: [],
  exerciseClass: '',
  settings: null,
  zielwort: '',
  audioButtonText: '',
  ablenkerFromSetJson: '',
  // ? Patient Specific code (when used in EigenTraining)
  isSliding: false,
  currentProgress: 0,
};

let timeoutReference = null;

const reducer = (state = DEFAULT_STATE, action) => {
  const { type, payload } = action;
  switch (type) {
    case 'CHANGE': {
      return { ...state, ...payload };
    }
    default: {
      return state;
    }
  }
};

function generateSettings(
  activePatient,
  exerciseClass,
  activatedExerciseModalities,
) {
  const settings = {};
  if (!activePatient) {
    activePatient = store.getState().activePatient;
  }
  let exerciseTypes = activePatient[exerciseClass + 'setExerciseTypes'];
  settings.Verstehen = {};
  settings.Verstehen.setting = exerciseTypes[0].setting;
  settings.Verstehen.value = exerciseTypes[0].value;
  settings.Verstehen.active = activatedExerciseModalities.Verstehen;
  settings.Verstehen.playbackRate = exerciseTypes[0].playbackRate;

  settings.Sprechen = {};
  settings.Sprechen.setting = exerciseTypes[1].setting;
  if (exerciseTypes[1].value) {
    settings.Sprechen.value = exerciseTypes[1].value;
  }
  settings.Sprechen.active = activatedExerciseModalities.Sprechen;
  settings.Sprechen.playbackRate = exerciseTypes[1].playbackRate;

  settings.Schreiben = {};
  settings.Schreiben.setting = exerciseTypes[2].setting;
  settings.Schreiben.value = exerciseTypes[2].value;
  settings.Schreiben.active = activatedExerciseModalities.Schreiben;
  settings.Schreiben.playbackRate = exerciseTypes[2].playbackRate;

  settings.Lesen = {};
  settings.Lesen.setting = exerciseTypes[3].setting;
  settings.Lesen.value = exerciseTypes[3].value;
  settings.Lesen.active = activatedExerciseModalities.Lesen;
  settings.Lesen.playbackRate = exerciseTypes[3].playbackRate;

  if (hasOwnProperty(activePatient, 'dailyPracticeDurationGoal')) {
    settings.dailyPracticeDurationGoal =
      activePatient.dailyPracticeDurationGoal;
  } else {
    settings.dailyPracticeDurationGoal = 60;
  }
  if (hasOwnProperty(activePatient, 'activateDailyGoal')) {
    settings.activateDailyGoal = activePatient.activateDailyGoal;
  }
  if (hasOwnProperty(activePatient, 'audioSpeedSetting')) {
    settings.audioSpeedSetting = activePatient.audioSpeedSetting;
  }
  return settings;
}

// Method to decide how Zielword button is named
function getZielwortName($scope) {
  if ($scope.exercises[$scope.currentExercise]?.exerciseClass === 'sentence') {
    return 'Zielsatz';
  } else {
    return 'Zielwort';
  }
}

export const useAufgabenExercise = (
  setId,
  isTextSet,
  patiendId,
  localWordsetProp,
  goHome,
  isSelfTraining,
  activatedExerciseModalities,
) => {
  const { showDialog, removeDialog } = useDialog();
  const { stopSound } = useAudioContext();
  const [state, dispatch] = useReducer(reducer, DEFAULT_STATE);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const localWordset = useMemo(() => cloneDeep(localWordsetProp), []);
  const { goBack } = useNavigation();
  const PAGER_OPTIONS = state.exercises.map((exercise) => exercise.id);

  const patient = useStoreState((store) => {
    return store.downloadPatients.find((patient) => patient._id == patiendId);
  });

  const activeExercise = state.exercises[state.currentExercise];
  const maxNumOfExercises = useMemo(() => {
    if (!isTextSet) return 0;
    const counter = state.exercises?.reduce((count, obj) => {
      if (obj.type === 'Verstehen') count++;
      return count;
    }, 0);
    return counter;
  }, [state.exercises?.length, isTextSet]);

  // functions
  const stateDispatcher = useCallback((payload) => {
    dispatch({
      type: 'CHANGE',
      payload,
    });
  }, []);

  const showResultsModalChange = useCallback((value) => {
    setShowResultsModal(value);
  }, []);

  const skip = () => {
    nextSlide(0, state.currentExercise, true);
  };

  const next = () => {
    nextSlide(0, state.currentExercise, false);
  };

  const isSolved = useCallback(() => {
    stateDispatcher({ nextButton: 'Weiter' });
  }, []);

  const notSolved = useCallback(() => {
    stateDispatcher({ nextButton: 'Überspringen' });
  }, []);

  //Method to slide to next exercise and log data
  const nextSlide = (sleep, exerciseid, skipped) => {
    if (isSelfTraining) {
      notSolved();
      if (!state.isSliding) {
        stateDispatcher({ isSliding: true });
        setTimeout(function () {
          checkProgress(skipped, true);
        }, sleep);
      }
    } else {
      if (typeof exerciseid === 'undefined') {
        exerciseid = state.currentExercise;
      }
      //add skipped result if exercise is skipped
      setTimeout(function () {
        const CONDITION = state.currentExercise + 1 === state.exercises.length;
        if (CONDITION) {
          const { exerciseClass } = activeExercise;
          if (exerciseClass === 'text') goBack();
          else setShowResultsModal(true);
          return;
        }
        // slide to next slide
        const newNextButtonValue = 'Überspringen';
        const newCurrentExerciseValue = parseInt(exerciseid) + 1;
        stateDispatcher({
          currentExercise: newCurrentExerciseValue,
          nextButton: newNextButtonValue,
        });
      }, sleep);
    }
  };

  const checkProgress = (skipped, goToNextSlide, isText) => {
    if (!skipped && state.settings.activateDailyGoal) {
      getTodaysProgress().then((data) => {
        if (data.showPopup === true) {
          if (isText) stopSound();
          stop().then(() => {
            stateDispatcher({
              currentProgress: 1,
            });
            const minutesExercised = data.stars + '0';
            showDialog(
              PREPARED_DIALOGS.starsProgressDialog({
                minutesExercised,
                stars: data.stars,
                patient,
                onConfirm: () => {
                  removeDialog();
                  onWeiterPress(isText ? false : goToNextSlide);
                },
                onStop: () => {
                  onHomePress();
                  removeDialog();
                },
              }),
            );
          });
        } else {
          stateDispatcher({
            currentProgress: data.progress * 0.95,
          });
          if (goToNextSlide && !isText) {
            nextSlideLogic();
          }
        }
      });
    } else {
      if (isText && !state.settings.activateDailyGoal) return;
      if (goToNextSlide) {
        nextSlideLogic();
      }
    }
  };

  // switch to next exercise
  const nextSlideLogic = () => {
    stateDispatcher({ isSliding: false });
    if (state.currentExercise + 1 === state.exercises.length) {
      // if star popup is off go back to menu
      if (!state.settings.activateDailyGoal) {
        stop().then(() => {
          uploadResults().then(() => {
            upload().then(() => {
              goHome();
            });
          });
        });
      }
      // else generate exercises again and repeat
      else {
        setTimeout(() => {
          init();
        });
      }
    } else {
      // slide to next slide
      stateDispatcher({
        currentExercise: state.currentExercise + 1,
      });
    }
  };

  const goSelect = () => {
    stop()
      .then(() => {
        getTodaysProgress().then((data) => {
          if (state.settings.activateDailyGoal && data.showPopup === true) {
            stateDispatcher({
              currentProgress: 1,
            });
            const minutesExercised = data.stars + '0';
            showDialog(
              PREPARED_DIALOGS.starsProgressDialog({
                minutesExercised,
                stars: data.stars,
                patient,
                onConfirm: () => {
                  removeDialog();
                  onWeiterPress(false);
                },
                onStop: () => {
                  onHomePress();
                  removeDialog();
                },
              }),
            );
          } else onGoSelect();
        });
      })
      .catch((error) => {
        logError('', error);
      });
  };

  const showZielwort = () => {
    let timeoutTime = 1000;
    let revealedName = '';
    if (state.exercises[state.currentExercise].exerciseClass === 'sentence') {
      timeoutTime = 2500;
      revealedName = state.exercises[state.currentExercise].sentence;
    } else {
      revealedName = state.exercises[state.currentExercise].word;
    }
    stateDispatcher({ zielwort: revealedName });
    const zielwortName = getZielwortName({
      exercises: state.exercises,
      currentExercise: state.currentExercise,
    });
    timeoutReference = setTimeout(function () {
      stateDispatcher({ zielwort: zielwortName });
    }, timeoutTime);
  };

  const init = () => {
    let settings = null;
    if (isSelfTraining) {
      settings = generateSettings(
        isPatientApp ? null : patient,
        localWordset.exerciseClass,
        activatedExerciseModalities,
      );
      // Persist self training selection in the store
      store
        .getActions()
        .changeSelfTrainingSelection(activatedExerciseModalities);
    } else {
      settings = localWordset.exerciseSettings;
    }
    const exercises = prepareJson(
      setId,
      localWordset,
      isSelfTraining ? settings : undefined,
    );

    let exerciseClass, zielwort, audioButtonText, ablenkerFromSetJson;
    // set exercise class to differ between text and words/sentences
    if (localWordset.exerciseClass) {
      exerciseClass = localWordset.exerciseClass;
    } else {
      exerciseClass = 'word';
    }

    ablenkerFromSetJson = localWordset.ablenkerFromSetJson;

    if (!isSelfTraining) {
      // add content of therapist toolbar and additional Ablenker from set for word/sentence exercises only
      if (exerciseClass === 'word' || exerciseClass === 'sentence') {
        zielwort = getZielwortName({
          exercises,
          currentExercise: state.currentExercise,
        });
        audioButtonText = zielwort;
      }
    }

    const payload = {
      exercises,
      settings,
      exerciseClass,
      zielwort,
      audioButtonText,
      ablenkerFromSetJson,
    };
    if (isSelfTraining) {
      payload.currentExercise = 0;
    }
    stateDispatcher(payload);
  };

  const onGoSelect = async () => {
    goHome();
    try {
      await uploadResults();
      await upload();
    } catch (error) {
      logError('', error);
    }
  };

  const onWeiterPress = async (goToNextSlide) => {
    try {
      await uploadResults();
      await upload();
      if (goToNextSlide) nextSlideLogic();
      const data = await getTodaysProgress();
      stateDispatcher({ currentProgress: data.progress * 0.95 });
    } catch (error) {
      logError('Could not upload on next slide', error);
    }
  };

  const onHomePress = () => {
    onGoSelect();
  };

  useEffect(() => {
    if (isSelfTraining) {
      applyUserId(patiendId);
      upload(false, false).then(() => {
        getTodaysProgress().then(function (data) {
          if (data.showPopup === true) {
            stateDispatcher({ currentProgress: 0 });
          } else {
            stateDispatcher({ currentProgress: data.progress * 0.95 });
          }
        });
      });
    }
    init();
    return () => {
      clearTimeout(timeoutReference);
      timeoutReference = null;
    };
  }, []);

  useEffect(() => {
    if (isSelfTraining) return;
    clearTimeout(timeoutReference);
    const zielwortName = getZielwortName({
      exercises: state.exercises,
      currentExercise: state.currentExercise,
    });
    stateDispatcher({ zielwort: zielwortName });
  }, [state.currentExercise, JSON.stringify(state.exercises)]);

  return {
    activeExercise,
    state,
    skip,
    next,
    PAGER_OPTIONS,
    showZielwort,
    isSolved,
    notSolved,
    maxNumOfExercises,
    // ? modal state
    showResultsModal,
    showResultsModalChange,
    // -----------
    // ? isSelf specific props
    touched,
    stop,
    goSelect,
    checkProgress,
  };
};
